import React, { useState } from 'react';
import { UPLOAD_ACCEPT, UPLOAD_MAX_FILES, uploadRefusal, uploadStoreFiles } from '../../api/upload';
import './forms.css';

/**
 * Attaching files to a form, through the one upload route this shop already stores its media with.
 *
 * A file is sent the moment it is chosen and what the field holds is the address the shop stored it
 * under, never the bytes: a review's photographs are `images: string[]` of URLs, so an attachment that
 * had not been uploaded by the time the form was submitted would be an attachment nothing keeps.
 *
 * What the route takes is what this offers and nothing more — JPEG, PNG, WebP, GIF and AVIF, at most
 * 8 MB each and at most ten of them. The three limits are the route's own; a file past any of them is
 * refused here with a sentence rather than sent to earn a 400.
 */

/** What the picker needs: where its answers go, what it may hold, and how it is announced. */
export type PhotoPickerProps = {
  id: string;
  buttonLabel: string;
  urls: readonly string[];
  onChange: (urls: readonly string[]) => void;
  /** How many attachments this field may hold at once; never more than the route's own ten. */
  limit: number;
  className?: string;
  invalid?: boolean;
  describedBy?: string;
};

/** What a visitor is told they may attach, in one sentence, before they pick anything at all. */
export const UPLOAD_HINT = 'JPEG, PNG, WebP, GIF or AVIF images, up to 8 MB each.';

// Bounds how many files one field may hold, so neither a merchant's typo nor a multi-select can pass ten.
export const attachmentLimit = (asked: number): number =>
  Math.min(Math.max(Math.trunc(asked) || 1, 1), UPLOAD_MAX_FILES);

// Splits a chosen set into the files this shop's upload route would take and the sentences for the rest.
function sortChosen(files: readonly File[]): { taking: File[]; refused: string[] } {
  const taking: File[] = [];
  const refused: string[] = [];
  for (const file of files) {
    const why = uploadRefusal(file);
    if (why) refused.push(why);
    else taking.push(file);
  }
  return { taking, refused };
}

// Draws what has already been attached, each with the way to take it off again.
const Attached: React.FC<{ urls: readonly string[]; onRemove: (url: string) => void }> = ({ urls, onRemove }) => (
  <ul className="xs-form-files">
    {urls.map((url) => (
      <li className="xs-form-file" key={url}>
        <img src={url} alt="" loading="lazy" />
        <button type="button" onClick={() => onRemove(url)}>Remove</button>
      </li>
    ))}
  </ul>
);

// Draws the picker: the button that opens it, whatever is attached, and why anything was refused.
export const PhotoPicker: React.FC<PhotoPickerProps> = (picker) => {
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<readonly string[]>([]);
  const room = attachmentLimit(picker.limit) - picker.urls.length;

  // Sends what the route would take, and says plainly why it would not take the rest.
  const choose = async (chosen: readonly File[]): Promise<void> => {
    const { taking, refused: why } = sortChosen(chosen.slice(0, Math.max(room, 0)));
    setRefused(why);
    if (taking.length === 0) return;
    setBusy(true);
    try {
      const stored = await uploadStoreFiles(taking);
      picker.onChange([...picker.urls, ...stored.map((file) => file.url)]);
    } catch (error) {
      setRefused([error instanceof Error ? error.message : 'The attachment could not be sent']);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label className={picker.className ? `xs-form-drop ${picker.className}` : 'xs-form-drop'}>
        <input
          className="xs-form-drop-input"
          id={picker.id}
          type="file"
          accept={UPLOAD_ACCEPT}
          multiple={attachmentLimit(picker.limit) > 1}
          disabled={busy || room <= 0}
          aria-invalid={picker.invalid ? true : undefined}
          aria-describedby={picker.describedBy}
          onChange={(event) => { void choose([...(event.target.files ?? [])]); event.target.value = ''; }}
        />
        <span>{busy ? 'Sending…' : picker.buttonLabel}</span>
      </label>
      {picker.urls.length === 0 ? null : (
        <Attached urls={picker.urls} onRemove={(url) => picker.onChange(picker.urls.filter((held) => held !== url))} />
      )}
      {refused.map((why) => <span className="xs-form-error" key={why} dir="auto">{why}</span>)}
    </div>
  );
};
